import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserService, UserRole } from '../admin/pages/users/userService';

// ─────────────────────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────────────────────
export const USER_KEYS = {
  all: ['users'] as const,
  lists: () => ['users', 'list'] as const,
  paginated: (page: number, search: string, role: string, status: string) =>
    ['users', 'list', page, search, role, status] as const,
  stats: () => ['users', 'stats'] as const,
};

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────
export interface UserPaginationParams {
  page: number;
  search: string;
  role: string;
  status: string;
  pageSize: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// useUsersPaginated
// ─────────────────────────────────────────────────────────────────────────────
export const useUsersPaginated = (params: UserPaginationParams) => {
  const roleFilter =
    params.role !== 'all' ? (params.role as UserRole) : undefined;
  const statusFilter =
    params.status === 'active' ? true : params.status === 'inactive' ? false : undefined;

  return useQuery({
    queryKey: USER_KEYS.paginated(params.page, params.search, params.role, params.status),
    queryFn: () =>
      UserService.getUsersPaginated(
        params.pageSize,
        params.page,
        params.search,
        roleFilter,
        statusFilter
      ),
    staleTime: 1000 * 60 * 2,
    placeholderData: (prev: any) => prev,
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('permission')) return false;
      return failureCount < 2;
    },
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useUserStats
// ─────────────────────────────────────────────────────────────────────────────
export const useUserStats = () => {
  return useQuery({
    queryKey: USER_KEYS.stats(),
    queryFn: () => UserService.getUserStats(),
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('permission')) return false;
      return failureCount < 2;
    },
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// useUserMutations
// ─────────────────────────────────────────────────────────────────────────────
export const useUserMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: USER_KEYS.all });
  };

  /**
   * Update a user's assigned role (dynamic) and active status.
   */
  const updateUser = useMutation({
    mutationFn: ({
      uid,
      roleId,
      roleName,
      legacyRole,
      isActive,
    }: {
      uid: string;
      roleId: string;
      roleName: string;
      legacyRole?: UserRole;
      isActive: boolean;
    }) =>
      Promise.all([
        UserService.updateUserRole(uid, roleId, roleName, legacyRole),
        UserService.toggleUserStatus(uid, isActive),
      ]),
    onSuccess: invalidateAll,
  });

  const toggleStatus = useMutation({
    mutationFn: ({ uid, isActive }: { uid: string; isActive: boolean }) =>
      UserService.toggleUserStatus(uid, isActive),
    onSuccess: invalidateAll,
  });

  const deleteUser = useMutation({
    mutationFn: ({ uid }: { uid: string }) => UserService.deleteUser(uid),
    onSuccess: invalidateAll,
  });

  return { updateUser, toggleStatus, deleteUser };
};


// import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
// import { UserService, UserRole } from '../admin/pages/users/userService';

// export const USER_KEYS = {
//   all: ['users'] as const,
//   lists: () => ['users', 'list'] as const,
//   paginated: (page: number, search: string, role: string, status: string) =>
//     ['users', 'list', page, search, role, status] as const,
//   stats: () => ['users', 'stats'] as const,
// };

// export interface UserPaginationParams {
//   page: number;
//   search: string;
//   role: string;
//   status: string;
//   pageSize: number;
// }

// export const useUsersPaginated = (params: UserPaginationParams) => {
//   return useQuery({
//     queryKey: USER_KEYS.paginated(params.page, params.search, params.role, params.status),
//     queryFn: () =>
//       UserService.getUsersPaginated(
//         params.pageSize,
//         params.page,
//         params.search || undefined,
//         params.role !== 'all' ? (params.role as UserRole) : undefined,
//         params.status !== 'all' ? params.status === 'active' : undefined
//       ),
//     staleTime: 1000 * 60 * 2,
//     placeholderData: (prev: any) => prev,
//     retry: (failureCount, error: any) => {
//       if (error?.message?.includes('permission') || error?.message?.includes('not have permission')) {
//         return false;
//       }
//       return failureCount < 1;
//     },
//   });
// };

// export const useUserStats = () => {
//   return useQuery({
//     queryKey: USER_KEYS.stats(),
//     queryFn: () => UserService.getUserStats(),
//     staleTime: 1000 * 60 * 3,
//     retry: (failureCount, error: any) => {
//       if (error?.message?.includes('permission') || error?.message?.includes('not have permission')) {
//         return false;
//       }
//       return failureCount < 1;
//     },
//   });
// };

// export const useUserMutations = () => {
//   const queryClient = useQueryClient();

//   const invalidateAll = () => {
//     queryClient.invalidateQueries({ queryKey: USER_KEYS.all });
//   };

//   const updateUser = useMutation({
//     mutationFn: async ({
//       uid,
//       role,
//       isActive,
//     }: {
//       uid: string;
//       role: UserRole;
//       isActive: boolean;
//     }) => {
//       await UserService.updateUserRole(uid, role);
//       await UserService.toggleUserStatus(uid, isActive);
//     },
//     onSuccess: invalidateAll,
//   });

//   const toggleStatus = useMutation({
//     mutationFn: ({ uid, isActive }: { uid: string; isActive: boolean }) =>
//       UserService.toggleUserStatus(uid, isActive),
//     onSuccess: invalidateAll,
//   });

//   const deleteUser = useMutation({
//     mutationFn: ({ uid }: { uid: string }) =>
//       UserService.deleteUser(uid),
//     onSuccess: invalidateAll,
//   });

//   return { updateUser, toggleStatus, deleteUser };
// };